import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { getJudgeCompetitionSummary, getJudgeQueue } from "../../../../../lib/scoring";
import type { JudgeCompetitionSummary } from "../../../../../lib/scoring";
import type { JudgeQueueRow } from "../../../../../types/database";
import { supabase } from "../../../../../lib/supabase";
import { FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Judge;
const FOREST_MID = THEME.surfaceBg;
const AMBER = THEME.accent;
const SAGE = THEME.success;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

const PAGE_MAX_W = 1240;

type CompetitionDetail = JudgeCompetitionSummary & {
  opens_at: string | null;
  closes_at: string | null;
  company_name: string | null;
};

type DeliverableRow = {
  id: string;
  name: string;
  sequence: number;
  rubric: string | null;
  due_at: string | null;
  is_locked_gate: boolean;
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function JudgeCompetitionScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const [summary, setSummary] = useState<CompetitionDetail | null>(null);
  const [queue, setQueue] = useState<JudgeQueueRow[]>([]);
  const [deliverables, setDeliverables] = useState<DeliverableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [briefOpen, setBriefOpen] = useState(false);

  const load = useCallback(async () => {
    if (typeof id !== "string") return;
    setLoading(true);
    const [summaryRow, queueRows, delRows] = await Promise.all([
      loadCompetitionDetail(id),
      getJudgeQueue(id),
      loadDeliverables(id),
    ]);
    setSummary(summaryRow);
    setQueue(queueRows);
    setDeliverables(delRows);
    setLoading(false);
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const grouped = useMemo(() => {
    const byDeliverable = new Map<string, JudgeQueueRow[]>();
    for (const r of queue) {
      const arr = byDeliverable.get(r.deliverable_id) ?? [];
      arr.push(r);
      byDeliverable.set(r.deliverable_id, arr);
    }
    // Sort deliverables by sequence; emit even if no submissions yet so the
    // judge can see "no submissions assigned to this deliverable yet" if
    // applicable.
    return [...deliverables]
      .sort((a, b) => a.sequence - b.sequence)
      .map((d) => ({
        deliverable: d,
        rows: (byDeliverable.get(d.id) ?? []).sort((a, b) =>
          a.team_name.localeCompare(b.team_name)
        ),
      }));
  }, [queue, deliverables]);

  const stats = useMemo(() => {
    const total = queue.length;
    const complete = queue.filter((r) => r.my_score != null).length;
    return { total, complete, pending: total - complete };
  }, [queue]);

  if (loading || !summary) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: PAGE_MAX_W }}>
        <BackLink to="/judge" label="← Back to assignments" />

        {/* Hero */}
        <View style={{ marginTop: 16, flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Judging
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.displayMedium,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          {summary.name}
        </Text>
        <Text
          style={{
            marginTop: 4,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            color: WM,
          }}
        >
          {summary.company_name ?? "Coravu"} · {stats.total} submissions assigned · {stats.complete} complete · {stats.pending} pending
        </Text>

        {/* Program context (collapsible) */}
        <View
          style={{
            marginTop: 20,
            padding: 18,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <Pressable
            onPress={() => setBriefOpen((o) => !o)}
            style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Program context
            </Text>
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.bodyMedium,
                fontSize: 12.5,
                color: LINEN,
              }}
            >
              {briefOpen ? "Hide program brief ▴" : "Show program brief ▾"}
            </Text>
          </Pressable>
          {briefOpen ? (
            <View style={{ marginTop: 14 }}>
              {summary.brief ? (
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    lineHeight: 21,
                    color: WS,
                  }}
                >
                  {summary.brief}
                </Text>
              ) : (
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    fontStyle: "italic",
                    color: WL,
                  }}
                >
                  No brief provided.
                </Text>
              )}
              <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 16 }}>
                <Meta label="Opens" value={fmtDate(summary.opens_at)} />
                <Meta label="Closes" value={fmtDate(summary.closes_at)} />
              </View>
            </View>
          ) : null}
        </View>

        {/* Submissions by deliverable */}
        <View style={{ marginTop: 28, gap: 22 }}>
          {grouped.map(({ deliverable, rows }) => (
            <DeliverableSection key={deliverable.id} deliverable={deliverable} rows={rows} competitionId={summary.id} />
          ))}
          {grouped.length === 0 ? (
            <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WM }}>
              No deliverables yet.
            </Text>
          ) : null}
        </View>
      </View>
    </ScrollView>
  );
}

function DeliverableSection({
  deliverable,
  rows,
  competitionId,
}: {
  deliverable: DeliverableRow;
  rows: JudgeQueueRow[];
  competitionId: string;
}) {
  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 18,
            color: LINEN,
          }}
        >
          {deliverable.sequence}. {deliverable.name}
        </Text>
        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 999,
            backgroundColor: "rgba(181,135,58,0.14)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            {rows.length} submission{rows.length === 1 ? "" : "s"}
          </Text>
        </View>
        {deliverable.due_at ? (
          <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WM }}>
            Due {fmtDate(deliverable.due_at)}
          </Text>
        ) : null}
      </View>

      {/* Rubric */}
      <View
        style={{
          marginTop: 10,
          padding: 14,
          borderRadius: 6,
          backgroundColor: "rgba(255,255,255,0.02)",
          borderWidth: 1,
          borderColor: BORDER,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            color: WM,
          }}
        >
          Rubric
        </Text>
        {deliverable.rubric?.trim() ? (
          <Text
            style={{
              marginTop: 6,
              fontFamily: FONTS.body,
              fontSize: 12.5,
              lineHeight: 19,
              color: WS,
            }}
          >
            {deliverable.rubric}
          </Text>
        ) : (
          <Text
            style={{
              marginTop: 6,
              fontFamily: FONTS.body,
              fontSize: 12,
              fontStyle: "italic",
              color: WL,
            }}
          >
            No rubric provided. Score on overall quality.
          </Text>
        )}
      </View>

      {/* Submission rows */}
      <View style={{ marginTop: 10, gap: 8 }}>
        {rows.map((r) => (
          <SubmissionRow key={r.submission_id} row={r} competitionId={competitionId} />
        ))}
        {rows.length === 0 ? (
          <Text style={{ fontFamily: FONTS.body, fontSize: 11.5, color: WL, fontStyle: "italic" }}>
            No submissions yet for this deliverable.
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function SubmissionRow({ row, competitionId }: { row: JudgeQueueRow; competitionId: string }) {
  const [hover, setHover] = useState(false);
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null;
  return (
    <Pressable
      onPress={() => router.push(`/judge/competition/${competitionId}/score/${row.submission_id}` as Href)}
      {...(Platform.OS === "web"
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        {
          padding: 14,
          borderRadius: 6,
          backgroundColor: hover ? "rgba(181,135,58,0.06)" : FOREST_MID,
          borderWidth: 1,
          borderColor: hover ? "rgba(181,135,58,0.30)" : BORDER,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
        } as ViewStyle,
        cursorWeb,
      ]}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: W }} numberOfLines={1}>
          {row.team_name}
        </Text>
        <Text
          style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}
          numberOfLines={1}
        >
          {row.file_name}
        </Text>
      </View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        {row.my_score != null ? (
          <Text
            style={{
              fontFamily: FONTS.displayMedium,
              fontSize: 18,
              color: SAGE,
            }}
          >
            {row.my_score}
          </Text>
        ) : null}
        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.04)",
            borderWidth: 1,
            borderColor: row.my_score == null ? AMBER : SAGE,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: row.my_score == null ? AMBER : SAGE,
            }}
          >
            {row.my_score == null ? "Pending" : "Scored"}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WL,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          marginTop: 2,
          fontFamily: FONTS.bodyMedium,
          fontSize: 12,
          color: LINEN,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function BackLink({ to, label }: { to: Href; label: string }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null;
  return (
    <Pressable
      onPress={() => {
        if (router.canGoBack()) router.back();
        else router.replace(to);
      }}
      style={[
        cursorWeb,
        { alignSelf: "flex-start", paddingVertical: 4 },
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 11.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

// ─── Loaders ───────────────────────────────────────────────────────────

async function loadCompetitionDetail(id: string): Promise<CompetitionDetail | null> {
  const summary = await getJudgeCompetitionSummary(id);
  if (!summary) return null;
  const { data } = await supabase
    .from("competitions")
    .select("opens_at, closes_at, companies(name)")
    .eq("id", id)
    .maybeSingle();
  type Embed = { name: string } | { name: string }[] | null;
  const row = (data ?? null) as { opens_at: string | null; closes_at: string | null; companies: Embed } | null;
  const company = row?.companies ? (Array.isArray(row.companies) ? row.companies[0] : row.companies) : null;
  return {
    ...summary,
    opens_at: row?.opens_at ?? null,
    closes_at: row?.closes_at ?? null,
    company_name: company?.name ?? null,
  };
}

async function loadDeliverables(competitionId: string): Promise<DeliverableRow[]> {
  const { data, error } = await supabase
    .from("deliverables")
    .select("id, name, sequence, rubric, due_at, is_locked_gate")
    .eq("competition_id", competitionId)
    .order("sequence", { ascending: true });
  if (error || !data) return [];
  return data as DeliverableRow[];
}
